<template>
  <b-card no-body class="sds-card">
    <div class="sds-accent" />

    <b-card-header class="sds-header">
      <div class="sds-header__left">
        <div class="sds-header__badge">
          <feather-icon icon="BarChart2Icon" size="18" />
        </div>
        <div>
          <h4 class="sds-header__title mb-0">Staff by Designation Strength</h4>
          <p class="sds-header__sub mb-0">{{ scopeHint }}</p>
        </div>
      </div>

      <div class="sds-header__right">
        <div class="sds-legend" aria-hidden="true">
          <span class="sds-legend__item"
            ><i class="is-male" /> Male</span
          >
          <span class="sds-legend__item"
            ><i class="is-female" /> Female</span
          >
          <span class="sds-legend__item"
            ><i class="is-other" /> Other</span
          >
        </div>
        <v-select
          v-if="isHeadOffice && campusSelectOptions.length"
          v-model="selectedCampusKey"
          :options="campusSelectOptions"
          :clearable="false"
          label="label"
          :reduce="(o) => o.value"
          placeholder="All campuses"
          append-to-body
          class="sds-select"
        />
        <button
          type="button"
          class="sds-icon-btn"
          :disabled="loading"
          v-b-tooltip.hover.top
          title="Refresh"
          @click="loadStats"
        >
          <b-spinner v-if="loading && hasLoadedOnce" small />
          <feather-icon v-else icon="RefreshCwIcon" size="15" />
        </button>
      </div>
    </b-card-header>

    <b-card-body class="sds-body">
      <!-- Skeleton -->
      <div v-if="loading && !hasLoadedOnce" class="sds-skeleton">
        <div class="sds-skel-kpis">
          <div v-for="n in 4" :key="'sk-' + n" class="sds-skel" />
        </div>
        <div class="sds-skel sds-skel--thin mb-1" />
        <div class="sds-skel-grid">
          <div v-if="isHeadOffice" class="sds-skel" style="height: 320px" />
          <div class="sds-skel" style="height: 320px" />
        </div>
      </div>

      <div v-else class="sds-content" :key="viewKey">
        <!-- Breadcrumb when campus drilled -->
        <div v-if="isHeadOffice && selectedCampus" class="sds-crumb">
          <button type="button" class="sds-crumb__back" @click="goAllCampuses">
            <feather-icon icon="ArrowLeftIcon" size="14" />
            All campuses
          </button>
          <span class="sds-crumb__sep">/</span>
          <span class="sds-crumb__now">{{ campusLabel(selectedCampus) }}</span>
        </div>

        <!-- KPI heroes -->
        <div class="sds-kpis">
          <div
            v-for="(kpi, idx) in kpiCards"
            :key="kpi.key"
            class="sds-kpi"
            :class="'sds-kpi--' + kpi.tone"
            :style="{ animationDelay: idx * 55 + 'ms' }"
          >
            <div class="sds-kpi__top">
              <span class="sds-kpi__label">{{ kpi.label }}</span>
              <span class="sds-kpi__icon">
                <feather-icon :icon="kpi.icon" size="15" />
              </span>
            </div>
            <div class="sds-kpi__value">
              {{ formatNum(kpi.value) }}
              <small v-if="kpi.pct !== null">{{ kpi.pct }}%</small>
            </div>
            <div class="sds-kpi__foot" v-if="kpi.hint">{{ kpi.hint }}</div>
          </div>
        </div>

        <div class="sds-track" v-if="activeSummary.total > 0">
          <span
            class="is-male"
            :style="{
              width: share(activeSummary.male, activeSummary.total) + '%',
            }"
          />
          <span
            class="is-female"
            :style="{
              width: share(activeSummary.female, activeSummary.total) + '%',
            }"
          />
          <span
            class="is-other"
            :style="{
              width: share(activeSummary.other, activeSummary.total) + '%',
            }"
          />
        </div>

        <div
          class="sds-panels"
          :class="{ 'sds-panels--single': !(isHeadOffice && selectedCampusKey === 'all') }"
        >
          <!-- Campuses panel (HO overview only) -->
          <section
            v-if="isHeadOffice && selectedCampusKey === 'all'"
            class="sds-panel"
          >
            <div class="sds-panel__head">
              <div>
                <h6 class="sds-panel__title">Campus Strength</h6>
                <span class="sds-panel__meta"
                  >{{ filteredCampusCards.length }} campuses</span
                >
              </div>
              <div class="sds-search">
                <feather-icon icon="SearchIcon" size="13" />
                <input
                  v-model="campusSearch"
                  type="text"
                  placeholder="Find campus"
                />
              </div>
            </div>

            <div class="sds-list">
              <button
                v-for="(campus, idx) in filteredCampusCards"
                :key="'c-' + campus.campusID"
                type="button"
                class="sds-row"
                :style="{ animationDelay: Math.min(idx, 12) * 28 + 'ms' }"
                @click="selectedCampusKey = String(campus.campusID)"
              >
                <div class="sds-row__rank">{{ idx + 1 }}</div>
                <div class="sds-row__body">
                  <div class="sds-row__line">
                    <span class="sds-row__name" :title="campusLabel(campus)">{{
                      campusLabel(campus)
                    }}</span>
                    <span class="sds-row__count">{{
                      formatNum(campus.total)
                    }}</span>
                  </div>
                  <div class="sds-row__bar">
                    <i
                      class="is-male"
                      :style="{
                        width: share(campus.male, campus.total) + '%',
                      }"
                    />
                    <i
                      class="is-female"
                      :style="{
                        width: share(campus.female, campus.total) + '%',
                      }"
                    />
                    <i
                      class="is-other"
                      :style="{
                        width: share(campus.other, campus.total) + '%',
                      }"
                    />
                  </div>
                  <div class="sds-row__stats">
                    <span class="is-male">{{ campus.male }} M</span>
                    <span class="is-female">{{ campus.female }} F</span>
                    <span class="is-other">{{ campus.other }} O</span>
                  </div>
                </div>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="14"
                  class="sds-row__chev"
                />
              </button>

              <div v-if="!filteredCampusCards.length" class="sds-empty">
                No campuses match your search
              </div>
            </div>
          </section>

          <!-- Designations panel -->
          <section class="sds-panel">
            <div class="sds-panel__head">
              <div>
                <h6 class="sds-panel__title">Designation Strength</h6>
                <span class="sds-panel__meta"
                  >{{ filteredDesignationRows.length }} roles · gender split</span
                >
              </div>
              <div class="sds-search">
                <feather-icon icon="SearchIcon" size="13" />
                <input
                  v-model="designationSearch"
                  type="text"
                  placeholder="Find designation"
                />
              </div>
            </div>

            <div class="sds-list sds-list--desig">
              <div
                v-for="(row, idx) in filteredDesignationRows"
                :key="'d-' + row.designation + '-' + idx"
                class="sds-row sds-row--static"
                :style="{ animationDelay: Math.min(idx, 14) * 28 + 'ms' }"
              >
                <div class="sds-row__rank sds-row__rank--soft">
                  {{ idx + 1 }}
                </div>
                <div class="sds-row__body">
                  <div class="sds-row__line">
                    <span class="sds-row__name" :title="row.designation">{{
                      row.designation
                    }}</span>
                    <span class="sds-row__count">{{
                      formatNum(row.count)
                    }}</span>
                  </div>
                  <div class="sds-row__bar sds-row__bar--tall">
                    <i
                      class="is-male"
                      :style="{
                        width: share(row.male, maxVisibleDesig) + '%',
                      }"
                      v-b-tooltip.hover.top
                      :title="`Male ${row.male}`"
                    />
                    <i
                      class="is-female"
                      :style="{
                        width: share(row.female, maxVisibleDesig) + '%',
                      }"
                      v-b-tooltip.hover.top
                      :title="`Female ${row.female}`"
                    />
                    <i
                      class="is-other"
                      :style="{
                        width: share(row.other, maxVisibleDesig) + '%',
                      }"
                      v-b-tooltip.hover.top
                      :title="`Other ${row.other}`"
                    />
                  </div>
                  <div class="sds-row__stats">
                    <span class="is-male"
                      >{{ row.male }} M
                      <em>({{ share(row.male, row.count) }}%)</em></span
                    >
                    <span class="is-female"
                      >{{ row.female }} F
                      <em>({{ share(row.female, row.count) }}%)</em></span
                    >
                    <span class="is-other"
                      >{{ row.other }} O
                      <em>({{ share(row.other, row.count) }}%)</em></span
                    >
                  </div>
                </div>
              </div>

              <div v-if="!filteredDesignationRows.length" class="sds-empty">
                No designation data found
              </div>
            </div>
          </section>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BSpinner,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";

export default {
  name: "StaffStatsCard",
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BSpinner,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  props: {
    isHeadOffice: { type: Boolean, default: false },
    campusId: { type: [Number, String], default: 0 },
    status: { type: String, default: "present" },
    dptId: { type: [Number, String], default: 0 },
  },
  data() {
    return {
      loading: false,
      hasLoadedOnce: false,
      selectedCampusKey: "all",
      campusSearch: "",
      designationSearch: "",
      summary: { total: 0, male: 0, female: 0, other: 0 },
      byDesignation: [],
      byCampus: [],
    };
  },
  computed: {
    viewKey() {
      return `${this.selectedCampusKey}-${this.activeSummary.total}-${this.filteredDesignationRows.length}`;
    },
    scopeHint() {
      if (!this.isHeadOffice) {
        return "Present staff strength by role and gender";
      }
      if (this.selectedCampus) {
        return `${this.campusLabel(this.selectedCampus)} · role & gender mix`;
      }
      return "All campuses overview · select a campus for details";
    },
    kpiCards() {
      const s = this.activeSummary;
      const total = s.total || 0;
      return [
        {
          key: "total",
          label: "Total Staff",
          value: s.total,
          pct: null,
          hint: this.isHeadOffice && !this.selectedCampus
            ? "Across network"
            : "In view",
          tone: "total",
          icon: "UsersIcon",
        },
        {
          key: "male",
          label: "Male",
          value: s.male,
          pct: this.share(s.male, total),
          hint: "of total",
          tone: "male",
          icon: "UserIcon",
        },
        {
          key: "female",
          label: "Female",
          value: s.female,
          pct: this.share(s.female, total),
          hint: "of total",
          tone: "female",
          icon: "UserIcon",
        },
        {
          key: "other",
          label: "Other",
          value: s.other,
          pct: this.share(s.other, total),
          hint: "of total",
          tone: "other",
          icon: "UserIcon",
        },
      ];
    },
    sortedCampuses() {
      return [...(this.byCampus || [])]
        .map((c) => ({
          ...c,
          total: Number(c.total) || 0,
          male: Number(c.male) || 0,
          female: Number(c.female) || 0,
          other: Number(c.other) || 0,
          byDesignation: Array.isArray(c.byDesignation) ? c.byDesignation : [],
        }))
        .sort(
          (a, b) =>
            b.total - a.total ||
            this.campusLabel(a).localeCompare(this.campusLabel(b))
        );
    },
    campusSelectOptions() {
      return [
        { value: "all", label: "All Campuses" },
        ...this.sortedCampuses.map((c) => ({
          value: String(c.campusID),
          label: `${this.campusLabel(c)} (${c.total})`,
        })),
      ];
    },
    selectedCampus() {
      if (!this.isHeadOffice || this.selectedCampusKey === "all") return null;
      return (
        this.sortedCampuses.find(
          (c) => String(c.campusID) === String(this.selectedCampusKey)
        ) || null
      );
    },
    activeSummary() {
      if (this.selectedCampus) {
        return {
          total: this.selectedCampus.total,
          male: this.selectedCampus.male,
          female: this.selectedCampus.female,
          other: this.selectedCampus.other,
        };
      }
      return this.summary;
    },
    activeDesignations() {
      const rows = this.selectedCampus
        ? this.selectedCampus.byDesignation
        : this.byDesignation;
      return (rows || [])
        .map((r) => ({
          designation: r.designation || "Unassigned",
          count: Number(r.count) || 0,
          male: Number(r.male) || 0,
          female: Number(r.female) || 0,
          other: Number(r.other) || 0,
        }))
        .sort((a, b) => b.count - a.count);
    },
    filteredCampusCards() {
      const q = String(this.campusSearch || "")
        .trim()
        .toLowerCase();
      if (!q) return this.sortedCampuses;
      return this.sortedCampuses.filter((c) =>
        this.campusLabel(c).toLowerCase().includes(q)
      );
    },
    filteredDesignationRows() {
      const q = String(this.designationSearch || "")
        .trim()
        .toLowerCase();
      if (!q) return this.activeDesignations;
      return this.activeDesignations.filter((r) =>
        String(r.designation).toLowerCase().includes(q)
      );
    },
    maxVisibleDesig() {
      return Math.max(
        ...this.filteredDesignationRows.slice(0, 40).map((r) => r.count),
        1
      );
    },
  },
  watch: {
    isHeadOffice() {
      this.selectedCampusKey = "all";
      this.hasLoadedOnce = false;
      this.loadStats();
    },
    campusId() {
      if (!this.isHeadOffice) {
        this.hasLoadedOnce = false;
        this.loadStats();
      }
    },
    selectedCampusKey() {
      this.designationSearch = "";
      this.campusSearch = "";
    },
  },
  created() {
    this.loadStats();
  },
  methods: {
    ...mapActions({ get: "get" }),

    goAllCampuses() {
      this.selectedCampusKey = "all";
    },

    formatNum(n) {
      return Number(n || 0).toLocaleString();
    },

    share(part, total) {
      const t = Number(total) || 0;
      if (!t) return 0;
      return Math.round((Number(part) / t) * 1000) / 10;
    },

    campusLabel(campus) {
      const name = String(campus?.campusName || "").trim();
      return name || `Campus ${campus?.campusID || "—"}`;
    },

    resolveCampusParam() {
      if (this.isHeadOffice) return 1;
      const id = Number(this.campusId) || 0;
      return id > 0 ? id : Number(this.$store.state.userData.cId) || 0;
    },

    async loadStats() {
      const campusID = this.resolveCampusParam();
      if (!campusID) return;

      this.loading = true;
      try {
        let url =
          this.$store.state.domain +
          "Staff/LoadStaffStats?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          campusID +
          "&status=" +
          encodeURIComponent(this.status || "present");

        const dpt = Number(this.dptId) || 0;
        if (dpt > 0) url += "&dptID=" + dpt;

        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        const payload =
          response && response.data && typeof response.data === "object"
            ? response.data
            : response && typeof response === "object"
            ? response
            : {};

        this.summary = {
          total: Number(payload.total) || 0,
          male: Number(payload.male) || 0,
          female: Number(payload.female) || 0,
          other: Number(payload.other) || 0,
        };
        this.byDesignation = Array.isArray(payload.byDesignation)
          ? payload.byDesignation
          : [];
        this.byCampus = Array.isArray(payload.byCampus) ? payload.byCampus : [];

        if (
          this.selectedCampusKey !== "all" &&
          !this.byCampus.some(
            (c) => String(c.campusID) === String(this.selectedCampusKey)
          )
        ) {
          this.selectedCampusKey = "all";
        }
      } catch (error) {
        console.error("Failed to load staff stats", error);
        this.summary = { total: 0, male: 0, female: 0, other: 0 };
        this.byDesignation = [];
        this.byCampus = [];
      } finally {
        this.loading = false;
        this.hasLoadedOnce = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sds-card {
  position: relative;
  margin-bottom: 1.25rem;
  border: 1px solid #ebe7ff;
  border-radius: 18px !important;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.07);
}

.sds-accent {
  height: 3px;
  background: linear-gradient(90deg, #7367f0 0%, #a78bfa 45%, #ec4899 100%);
}

.sds-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.85rem;
  padding: 1rem 1.1rem 0.85rem !important;
  border-bottom: 1px solid #f3f0ff;
  background: linear-gradient(180deg, #fcfbff 0%, #ffffff 100%);

  &__left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
  }

  &__badge {
    width: 42px;
    height: 42px;
    border-radius: 14px;
    display: grid;
    place-items: center;
    color: #7367f0;
    background: linear-gradient(145deg, #efeaff, #f8f5ff);
    border: 1px solid #e0d8ff;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
  }

  &__title {
    font-size: 1.12rem;
    font-weight: 800;
    color: #1e1b4b;
    letter-spacing: -0.02em;
  }

  &__sub {
    margin-top: 0.15rem;
    font-size: 0.75rem;
    color: #8b86b0;
  }

  &__right {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.55rem;
  }
}

.sds-legend {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
  background: #f8f7ff;
  border: 1px solid #ece8ff;

  &__item {
    display: inline-flex;
    align-items: center;
    gap: 0.28rem;
    font-size: 0.68rem;
    font-weight: 700;
    color: #64748b;

    i {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      display: inline-block;
    }
    .is-male {
      background: #3b82f6;
    }
    .is-female {
      background: #ec4899;
    }
    .is-other {
      background: #94a3b8;
    }
  }
}

.sds-select {
  min-width: 200px;
  max-width: 260px;
}

.sds-icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #e4dcff;
  background: #fff;
  color: #7367f0;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: 0.15s ease;

  &:hover:not(:disabled) {
    background: #f5f3ff;
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.sds-body {
  padding: 0.95rem 1.1rem 1.15rem !important;
}

/* Skeleton */
.sds-skel-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.65rem;
  margin-bottom: 0.75rem;
}
.sds-skel-grid {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 0.75rem;
}
.sds-skel {
  border-radius: 14px;
  height: 84px;
  background: linear-gradient(90deg, #f3f0ff, #faf9ff, #f3f0ff);
  background-size: 200% 100%;
  animation: sdsPulse 1.3s ease-in-out infinite;

  &--thin {
    height: 8px;
    border-radius: 999px;
  }
}
@keyframes sdsPulse {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.sds-content {
  animation: sdsIn 0.4s ease;
}
@keyframes sdsIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.sds-crumb {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.85rem;

  &__back {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    border: 0;
    background: #f3f0ff;
    color: #5b4cdb;
    font-size: 0.76rem;
    font-weight: 700;
    border-radius: 999px;
    padding: 0.32rem 0.75rem;
    cursor: pointer;
    transition: 0.15s ease;

    &:hover {
      background: #ebe5ff;
    }
  }

  &__sep {
    color: #c4b5fd;
    font-weight: 700;
  }

  &__now {
    font-size: 0.84rem;
    font-weight: 800;
    color: #1e1b4b;
  }
}

.sds-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.65rem;
  margin-bottom: 0.65rem;
}

.sds-kpi {
  position: relative;
  border-radius: 16px;
  padding: 0.85rem 0.9rem;
  border: 1px solid transparent;
  overflow: hidden;
  animation: sdsRise 0.4s ease both;

  &::after {
    content: "";
    position: absolute;
    inset: auto -20% -40% auto;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    opacity: 0.18;
    pointer-events: none;
  }

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.35rem;
  }

  &__label {
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #7c779c;
  }

  &__icon {
    width: 28px;
    height: 28px;
    border-radius: 9px;
    display: grid;
    place-items: center;
    background: rgba(255, 255, 255, 0.7);
  }

  &__value {
    font-size: 1.45rem;
    font-weight: 800;
    line-height: 1.1;
    color: #0f172a;
    letter-spacing: -0.02em;

    small {
      margin-left: 0.35rem;
      font-size: 0.78rem;
      font-weight: 700;
      color: #64748b;
    }
  }

  &__foot {
    margin-top: 0.2rem;
    font-size: 0.68rem;
    font-weight: 600;
    color: #94a3b8;
  }

  &--total {
    background: linear-gradient(145deg, #f4f0ff, #faf8ff);
    border-color: #e4dcff;
    .sds-kpi__icon {
      color: #7367f0;
    }
    &::after {
      background: #7367f0;
    }
  }
  &--male {
    background: linear-gradient(145deg, #eff6ff, #f8fbff);
    border-color: #dbeafe;
    .sds-kpi__value {
      color: #1d4ed8;
    }
    .sds-kpi__icon {
      color: #3b82f6;
    }
    &::after {
      background: #3b82f6;
    }
  }
  &--female {
    background: linear-gradient(145deg, #fdf2f8, #fff7fb);
    border-color: #fce7f3;
    .sds-kpi__value {
      color: #be185d;
    }
    .sds-kpi__icon {
      color: #ec4899;
    }
    &::after {
      background: #ec4899;
    }
  }
  &--other {
    background: linear-gradient(145deg, #f8fafc, #ffffff);
    border-color: #e2e8f0;
    .sds-kpi__value {
      color: #475569;
    }
    .sds-kpi__icon {
      color: #64748b;
    }
    &::after {
      background: #94a3b8;
    }
  }
}

@keyframes sdsRise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.sds-track {
  display: flex;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: #eef2f7;
  margin-bottom: 1rem;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);

  span {
    display: block;
    height: 100%;
    transition: width 0.55s ease;
  }
  .is-male {
    background: linear-gradient(90deg, #60a5fa, #3b82f6);
  }
  .is-female {
    background: linear-gradient(90deg, #f472b6, #ec4899);
  }
  .is-other {
    background: linear-gradient(90deg, #cbd5e1, #94a3b8);
  }
}

.sds-panels {
  display: grid;
  grid-template-columns: minmax(260px, 0.95fr) minmax(0, 1.25fr);
  gap: 0.85rem;

  &--single {
    grid-template-columns: 1fr;
  }
}

.sds-panel {
  border: 1px solid #efeaff;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #fcfbff 100%);
  padding: 0.8rem;
  min-height: 0;
  box-shadow: 0 4px 16px rgba(115, 103, 240, 0.04);

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.65rem;
    flex-wrap: wrap;
    margin-bottom: 0.7rem;
  }

  &__title {
    margin: 0;
    font-size: 0.82rem;
    font-weight: 800;
    color: #312e81;
    letter-spacing: 0.01em;
  }

  &__meta {
    display: block;
    margin-top: 0.1rem;
    font-size: 0.68rem;
    font-weight: 600;
    color: #a5a0c4;
  }
}

.sds-search {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 150px;
  max-width: 190px;
  height: 32px;
  padding: 0 0.65rem;
  border-radius: 999px;
  border: 1px solid #e4dcff;
  background: #fff;
  color: #a78bfa;

  input {
    border: 0;
    outline: 0;
    width: 100%;
    font-size: 0.75rem;
    color: #334155;
    background: transparent;

    &::placeholder {
      color: #b4adcf;
    }
  }
}

.sds-list {
  max-height: 390px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding-right: 0.15rem;

  &--desig {
    max-height: 420px;
  }

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ddd6fe;
    border-radius: 999px;
  }
}

.sds-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  text-align: left;
  border: 1px solid #f0edff;
  background: #fff;
  border-radius: 12px;
  padding: 0.55rem 0.6rem;
  cursor: pointer;
  animation: sdsRise 0.35s ease both;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover {
    border-color: #c4b5fd;
    box-shadow: 0 8px 18px rgba(115, 103, 240, 0.1);
    transform: translateY(-1px);
  }

  &--static {
    cursor: default;
    &:hover {
      transform: none;
      box-shadow: none;
      border-color: #f0edff;
    }
  }

  &__rank {
    width: 26px;
    height: 26px;
    border-radius: 8px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    font-size: 0.68rem;
    font-weight: 800;
    color: #7367f0;
    background: #f3f0ff;

    &--soft {
      background: #f8fafc;
      color: #64748b;
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__line {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.28rem;
  }

  &__name {
    font-size: 0.8rem;
    font-weight: 700;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__count {
    font-size: 0.84rem;
    font-weight: 800;
    color: #4c1d95;
    flex-shrink: 0;
  }

  &__bar {
    display: flex;
    height: 6px;
    border-radius: 999px;
    overflow: hidden;
    background: #f1f5f9;
    margin-bottom: 0.28rem;

    &--tall {
      height: 9px;
    }

    i {
      display: block;
      height: 100%;
      transition: width 0.45s ease;
    }
    .is-male {
      background: #3b82f6;
    }
    .is-female {
      background: #ec4899;
    }
    .is-other {
      background: #94a3b8;
    }
  }

  &__stats {
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
    font-size: 0.66rem;
    font-weight: 700;

    .is-male {
      color: #2563eb;
    }
    .is-female {
      color: #db2777;
    }
    .is-other {
      color: #64748b;
    }

    em {
      font-style: normal;
      font-weight: 600;
      opacity: 0.75;
    }
  }

  &__chev {
    color: #c4b5fd;
    flex-shrink: 0;
  }
}

.sds-empty {
  text-align: center;
  color: #a5a0c4;
  font-size: 0.82rem;
  padding: 1.5rem 0.5rem;
}

@media (max-width: 991.98px) {
  .sds-panels {
    grid-template-columns: 1fr;
  }
  .sds-skel-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767.98px) {
  .sds-kpis,
  .sds-skel-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .sds-legend {
    display: none;
  }
  .sds-select {
    min-width: 160px;
  }
}
</style>
